import { useEffect, useRef } from 'react'

// The app's one focus-containment mechanism for the hand-rolled dialogs — no
// per-dialog copy, no new dependency, and no modification to
// `@atlas/ui-primitives`. `aria-modal="true"` alone does not contain focus, and
// neither does initial focus plus focus return alone; this hook additionally
// wraps Tab/Shift+Tab at the dialog's boundary and redirects any focus that
// escapes to a background control back inside.
//
// Lives here rather than beside one feature's dialogs because the campaign
// dialogs and the article translate dialog both use it.

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusableElements(container: HTMLElement): HTMLElement[] {
	return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
}

/**
 * Contains keyboard focus inside the returned ref's element while `isOpen` is
 * true: moves focus inside on open (to `[data-autofocus]` if the dialog marks
 * one, else the first focusable control), wraps Tab/Shift+Tab at the first/last
 * focusable control, redirects focus that escapes to the background back
 * inside, and returns focus to the triggering control on close.
 */
export function useDialogFocusTrap(isOpen: boolean) {
	const containerRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<Element | null>(null)

	useEffect(() => {
		if (!isOpen) return
		triggerRef.current = document.activeElement
		const node = containerRef.current
		if (!node) return

		// A dialog may nominate what receives focus on open. Without that the first
		// focusable control gets it, which is wrong when that control reacts to focus —
		// a Combobox opens its listbox and covers the dialog's own buttons.
		const nominated = node.querySelector<HTMLElement>('[data-autofocus]')
		const initial = getFocusableElements(node)
		;(nominated ?? initial[0] ?? node).focus()

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== 'Tab') return
			const container = containerRef.current
			if (!container) return
			const elements = getFocusableElements(container)
			if (elements.length === 0) return
			const first = elements[0]
			const last = elements[elements.length - 1]
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first.focus()
			}
		}

		function handleFocusIn(event: FocusEvent) {
			const container = containerRef.current
			if (!container) return
			if (event.target instanceof Node && container.contains(event.target)) return
			const elements = getFocusableElements(container)
			;(elements[0] ?? container).focus()
		}

		document.addEventListener('keydown', handleKeyDown)
		document.addEventListener('focusin', handleFocusIn)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			document.removeEventListener('focusin', handleFocusIn)
			if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus()
		}
	}, [isOpen])

	return containerRef
}
